import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

import {
  CreatePitstopPayload,
  Pitstop,
  PitstopType,
  UpdatePitstopPayload,
  getListPitstop,
} from './pitstops';

export type HubStatus = 'active' | 'inactive';
export type HubStatusFilter = HubStatus | 'all';

/**
 * Logistic-app view of a "hub": a HUB-type pitstop served by the be-electric
 * pitstop service. Field names are kept stable (id, code, name, address,
 * latitude, longitude) so the rest of the logistic UI doesn't need to know
 * the underlying store changed. `id` is numeric (pitstop's primary key).
 */
export interface Hub {
  id: number;
  uuid: string;
  code: string;
  name: string;
  address: string | null;
  latitude: number;
  longitude: number;
  /** Pitstop has no status — always reported as 'active' for compatibility. */
  status: HubStatus;
  clockInRadius: number;
  coverageRadius: number;
  phoneNumber: string | null;
  hasStaff: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateHubPayload {
  code: string;
  name: string;
  address?: string | null;
  latitude: number;
  longitude: number;
  clockInRadius?: number;
  coverageRadius?: number;
  phoneNumber?: string | null;
}

export interface UpdateHubPayload {
  name?: string;
  address?: string | null;
  latitude?: number;
  longitude?: number;
  clockInRadius?: number;
  coverageRadius?: number;
  phoneNumber?: string | null;
  /** Pitstop has no status field; setting this here is a no-op. */
  status?: HubStatus;
}

interface PitstopListEnvelope {
  status: number;
  message?: string;
  data: Pitstop[];
  pagination?: unknown;
}

type Envelope<T> = { status: number; message?: string; data: T };
const unwrap = <T>(payload: Envelope<T> | T): T =>
  payload && typeof payload === 'object' && 'data' in (payload as any)
    ? (payload as Envelope<T>).data
    : (payload as T);

/**
 * Pitstop → Hub. Exported because the zones page maps pitstops it fetched
 * itself (targeted `?hub=` / already-zoned lookups) and must not drift from
 * the mapping the list calls here use.
 */
export const pitstopToHub = (p: Pitstop): Hub => ({
  id: p.id,
  uuid: p.uuid,
  code: p.code,
  name: p.name,
  address: p.address,
  latitude: Number(p.latitude),
  longitude: Number(p.longitude),
  status: 'active',
  clockInRadius: p.clockInRadius,
  coverageRadius: p.coverageRadius,
  phoneNumber: p.phoneNumber,
  hasStaff: p.hasStaff,
  createdAt: p.createdAt,
  updatedAt: p.updatedAt,
});

/** What the zones picker needs back from a server-side location search. */
export interface ZoneLocationSearch {
  hubs: Hub[];
  pitstops: Hub[];
  /** Owning client name per pitstop id. */
  clientNames: Record<number, string>;
}

export const hubsAPI = {
  /**
   * Server-side search across every location the zones editor can draw on —
   * Dash hubs and client pitstops alike.
   *
   * The picker can't filter what it holds: it only ever loads the first page
   * of each type, so anything past that is unreachable unless core is asked.
   */
  searchLocations: async (search: string): Promise<ZoneLocationSearch> => {
    try {
      const [hubRes, pitstopRes] = await Promise.all([
        getListPitstop({
          type: PitstopType.HUB,
          hasStaff: true,
          page: '1',
          size: '25',
          search,
        }),
        getListPitstop({
          type: 'PROVIDER_WAREHOUSE',
          page: '1',
          size: '25',
          search,
        }),
      ]);
      const rawPitstops = pitstopRes.data ?? [];
      return {
        hubs: (hubRes.data ?? []).map(pitstopToHub),
        pitstops: rawPitstops.map(pitstopToHub),
        clientNames: Object.fromEntries(
          rawPitstops.map((p: Pitstop) => [p.id, p.provider?.name ?? ''])
        ),
      };
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Lists HUB-type pitstops with staff. The `status` argument is accepted for
   * call-site compatibility but ignored — pitstop has no active/inactive
   * flag, so every list returns the same set.
   */
  list: async (_status: HubStatusFilter = 'active'): Promise<Hub[]> => {
    try {
      const response = await Axios.get('/v1/pitstop', {
        params: {
          page: 1,
          size: 100,
          type: PitstopType.HUB,
          hasStaff: true,
        },
      });
      const envelope = response.data as PitstopListEnvelope;
      return (envelope.data ?? []).map(pitstopToHub);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  getById: async (id: number): Promise<Hub> => {
    try {
      const response = await Axios.get(`/v1/pitstop/${id}`);
      return pitstopToHub(unwrap<Pitstop>(response.data));
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  create: async (payload: CreateHubPayload): Promise<Hub> => {
    try {
      const body: CreatePitstopPayload = {
        code: payload.code,
        name: payload.name,
        address: payload.address ?? '',
        latitude: payload.latitude,
        longitude: payload.longitude,
        pitstopType: PitstopType.HUB,
        clockInRadius: payload.clockInRadius ?? 5000,
        coverageRadius: payload.coverageRadius ?? payload.clockInRadius ?? 5000,
        hasStaff: true,
        ...(payload.phoneNumber ? { phoneNumber: payload.phoneNumber } : {}),
      };
      const response = await Axios.post('/v1/pitstop', body);
      return pitstopToHub(unwrap<Pitstop>(response.data));
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  update: async (id: number, payload: UpdateHubPayload): Promise<Hub> => {
    try {
      const body: UpdatePitstopPayload = {
        ...(payload.name !== undefined ? { name: payload.name } : {}),
        ...(payload.address !== undefined
          ? { address: payload.address ?? '' }
          : {}),
        ...(payload.latitude !== undefined
          ? { latitude: payload.latitude }
          : {}),
        ...(payload.longitude !== undefined
          ? { longitude: payload.longitude }
          : {}),
        ...(payload.clockInRadius !== undefined
          ? { clockInRadius: payload.clockInRadius }
          : {}),
        ...(payload.coverageRadius !== undefined
          ? { coverageRadius: payload.coverageRadius }
          : {}),
        ...(payload.phoneNumber !== undefined
          ? { phoneNumber: payload.phoneNumber }
          : {}),
      };
      const response = await Axios.put(`/v1/pitstop/${id}`, body);
      return pitstopToHub(unwrap<Pitstop>(response.data));
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },
};

export const HUB_STATUS_LABELS: Record<HubStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
};
